import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ConfigRow {
  readonly configId: string;
  readonly configKey: string;
  readonly category: string;
  readonly configValue: string;
  readonly isSecret: boolean;
}

export function M19ConfigurationPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ConfigRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  const [category, setCategory] = useState('');
  const [configKey, setConfigKey] = useState('');
  const [configValue, setConfigValue] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: ConfigRow[] }>('/v1/platform/configurations', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/platform/configurations', {
        method: 'POST', token,
        body: { category, configKey, configValue: configValue || undefined, isSecret },
      });
      setCreated(true);
      setCategory('');
      setConfigKey('');
      setConfigValue('');
      setIsSecret(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const saveValue = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await apiRequest(`/v1/platform/configurations/${id}`, {
        method: 'PATCH', token, body: { configValue: editValues[id] ?? '' },
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m19.configuration.loading')}</p>;
  return (
    <main aria-label="configuration">
      <h1>{t('m19.configuration.title')}</h1>

      <form onSubmit={onCreate} aria-label="configuration-create-form">
        <h2>{t('m19.configuration.newConfig')}</h2>
        <label htmlFor="cfg-category">{t('m19.configuration.labelCategory')}</label>
        <input id="cfg-category" value={category} onChange={(e) => setCategory(e.target.value)} required />
        <label htmlFor="cfg-key">{t('m19.configuration.labelKey')}</label>
        <input id="cfg-key" value={configKey} onChange={(e) => setConfigKey(e.target.value)} required />
        <label htmlFor="cfg-value">{t('m19.configuration.labelValue')}</label>
        <input id="cfg-value" value={configValue} onChange={(e) => setConfigValue(e.target.value)} />
        <label htmlFor="cfg-secret">
          <input id="cfg-secret" type="checkbox" checked={isSecret} onChange={(e) => setIsSecret(e.target.checked)} />
          {t('m19.configuration.secret')}
        </label>
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('m19.configuration.created')}</p> : null}
        <button type="submit" disabled={creating}>{creating ? t('m19.configuration.creating') : t('m19.configuration.create')}</button>
      </form>

      <table aria-label="configurations-table">
        <thead><tr><th>{t('m19.configuration.colKey')}</th><th>{t('m19.configuration.colCategory')}</th><th>{t('m19.configuration.colValue')}</th><th>{t('m19.configuration.colSecret')}</th><th>{t('m19.configuration.colEdit')}</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.configId}>
              <td>{c.configKey}</td>
              <td>{c.category}</td>
              <td>{c.isSecret ? '***' : c.configValue}</td>
              <td>{c.isSecret ? t('m19.configuration.yes') : t('m19.configuration.no')}</td>
              <td>
                <input
                  aria-label={`edit-value-${c.configId}`}
                  value={editValues[c.configId] ?? ''}
                  placeholder={t('m19.configuration.placeholderNewValue')}
                  onChange={(e) => setEditValues({ ...editValues, [c.configId]: e.target.value })}
                />
                <button type="button" aria-label={`save-${c.configId}`} disabled={busyId === c.configId} onClick={() => void saveValue(c.configId)}>
                  {busyId === c.configId ? t('m19.configuration.saving') : t('m19.configuration.save')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
